<!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
<script setup>
import { computed, onMounted, ref } from 'vue';
import { RouterLink } from 'vue-router';
import { useAccount } from 'dashboard/composables/useAccount';
import { useAlert } from 'dashboard/composables';
import { useMapGetter } from 'dashboard/composables/store';
import { buildRealEstateCapabilityContext } from 'dashboard/helper/permissionsHelper';
import { REAL_ESTATE_SURFACES } from 'dashboard/constants/permissions';
import PropertyDemandsAPI from 'dashboard/api/propertyDemands';
import PropertyMatchesAPI from 'dashboard/api/propertyMatches';
import LaisHeader from 'dashboard/components-next/LaisHeader.vue';
import LaisPermissionState from 'dashboard/components-next/LaisPermissionState.vue';
import LaisPrimaryButton from 'dashboard/components-next/LaisPrimaryButton.vue';

const STATUS_LABELS = {
  active: 'Ativa',
  paused: 'Pausada',
  satisfied: 'Satisfeita',
  expired: 'Expirada',
  discarded: 'Descartada',
};
const STATUS_CLASSES = {
  active: 'bg-[#EAF6F1] text-[#176B55]',
  paused: 'bg-[#FFF4D8] text-[#8A5A00]',
  satisfied: 'bg-[#EAF7EA] text-[#2D6A2D]',
  expired: 'bg-[#F2F4F7] text-[#5F6673]',
  discarded: 'bg-[#FDEBEC] text-[#B42318]',
};
const MODALIDADE_LABELS = {
  venda: 'Compra',
  aluguel: 'Locacao',
  temporada: 'Temporada',
};
const OBJECTIVE_LABELS = {
  home: 'Moradia',
  investment: 'Investimento',
  family: 'Familia',
  relocation: 'Mudanca',
  temporary: 'Temporario',
  other: 'Outro',
};
const SLO_CLASSES = {
  ok: 'bg-[#EAF6F1] text-[#176B55]',
  breached: 'bg-[#FDEBEC] text-[#B42318]',
  no_data: 'bg-[#F2F4F7] text-[#5F6673]',
};
const ACTION_LABELS = {
  pause: 'Pausar',
  resume: 'Retomar',
  recompute_matches: 'Recalcular',
  supersede: 'Supersede',
};
const ACTION_ICONS = {
  pause: 'i-lucide-pause-circle',
  resume: 'i-lucide-play-circle',
  recompute_matches: 'i-lucide-refresh-cw',
  supersede: 'i-lucide-git-branch-plus',
};

const defaultFilters = () => ({
  status: '',
  modalidade: '',
  objective: '',
  source: '',
  q: '',
  page: 1,
});

const account = useAccount();
const filters = ref(defaultFilters());
const demands = ref([]);
const matches = ref([]);
const meta = ref({ count: 0, current_page: 1 });
const capabilityError = ref(null);
const currentAccount = useMapGetter('getCurrentAccount');
// FE-AUD-107: gate negativo de permissao antes de buscar demandas de clientes
// (contato/orcamento sensiveis). A capability canonica de clientes autoriza a
// leitura; um 403 do backend marca o contexto como negado e bloqueia recargas.
const capabilitiesContext = computed(() =>
  buildRealEstateCapabilityContext({
    surface: REAL_ESTATE_SURFACES.LEADS_INDEX,
    account: currentAccount.value,
    meta: meta.value,
    error: capabilityError.value,
  })
);
const canViewDemands = computed(
  () => capabilitiesContext.value.actions.view?.visible !== false
);
const snapshot = ref(null);
const selectedIds = ref(new Set());
const selectedDemandId = ref(null);
const bulkReason = ref('');
const isLoading = ref(false);
const isLoadingSnapshot = ref(false);
const isLoadingMatches = ref(false);
const isMutating = ref(false);
const mutatingKey = ref('');

const hasDemands = computed(() => demands.value.length > 0);
const selectedCount = computed(() => selectedIds.value.size);
const selectedDemand = computed(
  () =>
    demands.value.find(demand => demand.id === selectedDemandId.value) || null
);
const selectedDemandMatches = computed(() => matches.value.slice(0, 5));
const snapshotMetrics = computed(() => snapshot.value?.metrics || {});
const snapshotSlo = computed(() => snapshot.value?.slo || {});
const sloRows = computed(() => [
  { key: 'freshness', label: 'Freshness', value: snapshotSlo.value.freshness },
  {
    key: 'explainability',
    label: 'Explainability',
    value: snapshotSlo.value.explainability,
  },
  {
    key: 'rejected_ratio',
    label: 'Rejeicao',
    value: snapshotSlo.value.rejected_ratio,
  },
]);
const anySloBreached = computed(() =>
  sloRows.value.some(row => row.value === 'breached')
);
const stats = computed(() => [
  {
    key: 'total',
    label: 'Demandas filtradas',
    value: meta.value.count ?? demands.value.length,
    icon: 'i-lucide-list-filter',
  },
  {
    key: 'active',
    label: 'Ativas na pagina',
    value: demands.value.filter(demand => demand.status === 'active').length,
    icon: 'i-lucide-circle-dot',
  },
  {
    key: 'paused',
    label: 'Pausadas na pagina',
    value: demands.value.filter(demand => demand.status === 'paused').length,
    icon: 'i-lucide-pause-circle',
  },
  {
    key: 'slo',
    label: 'SLO local P11',
    value: anySloBreached.value
      ? 'breach'
      : snapshotSlo.value.freshness || 'no_data',
    icon: anySloBreached.value ? 'i-lucide-alarm-clock' : 'i-lucide-gauge',
  },
]);

const labelFor = (collection, key) => collection[key] || key || 'Nao informado';
const statusLabel = status => labelFor(STATUS_LABELS, status);
const modalidadeLabel = modalidade => labelFor(MODALIDADE_LABELS, modalidade);
const objectiveLabel = objective => labelFor(OBJECTIVE_LABELS, objective);
const badgeClass = (collection, key) =>
  collection[key] || 'bg-[#F2F4F7] text-[#5F6673]';
const money = value => {
  if (value === null || value === undefined || value === '') return 'Sem valor';
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    maximumFractionDigits: 0,
  }).format(Number(value));
};
const dateTime = value => {
  if (!value) return 'Sem data';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return 'Data invalida';
  return new Intl.DateTimeFormat('pt-BR', {
    dateStyle: 'short',
    timeStyle: 'short',
  }).format(parsed);
};
const demandBudget = demand => {
  if (!demand?.budget_min && !demand?.budget_max) return 'Sem orcamento';
  if (demand.budget_min && demand.budget_max) {
    return `${money(demand.budget_min)} - ${money(demand.budget_max)}`;
  }
  return demand.budget_max
    ? `ate ${money(demand.budget_max)}`
    : `desde ${money(demand.budget_min)}`;
};
const demandRegion = demand =>
  [demand?.regions?.[0], demand?.property_types?.[0]]
    .filter(Boolean)
    .join(' · ') || 'Sem criterio';
const canRun = (demand, action) =>
  Array.isArray(demand?.available_actions) &&
  demand.available_actions.includes(action);
const selectedIdsArray = () => Array.from(selectedIds.value);
const loadMatches = async () => {
  if (!selectedDemandId.value) {
    matches.value = [];
    return;
  }

  isLoadingMatches.value = true;
  try {
    const { data } = await PropertyMatchesAPI.list({
      property_demand_id: selectedDemandId.value,
      page: 1,
    });
    matches.value = data.data || [];
  } catch {
    matches.value = [];
  } finally {
    isLoadingMatches.value = false;
  }
};
const selectDemand = async demand => {
  selectedDemandId.value = demand?.id || null;
  await loadMatches();
};
const toggleDemand = id => {
  const next = new Set(selectedIds.value);
  if (next.has(id)) next.delete(id);
  else next.add(id);
  selectedIds.value = next;
};
const toggleAll = () => {
  selectedIds.value =
    selectedCount.value === demands.value.length
      ? new Set()
      : new Set(demands.value.map(demand => demand.id));
};
const clearSelection = () => {
  selectedIds.value = new Set();
};
const listParams = () => ({ ...filters.value });
const loadDemands = async () => {
  if (!canViewDemands.value) {
    capabilityError.value = { status: 403 };
    demands.value = [];
    return;
  }
  isLoading.value = true;
  try {
    const { data } = await PropertyDemandsAPI.list(listParams());
    demands.value = data.data || [];
    meta.value = data.meta || { count: demands.value.length, current_page: 1 };
    capabilityError.value = null;
    selectedIds.value = new Set(
      selectedIdsArray().filter(id =>
        demands.value.some(demand => demand.id === id)
      )
    );

    const nextDemand =
      demands.value.find(demand => demand.id === selectedDemandId.value) ||
      demands.value[0] ||
      null;
    selectedDemandId.value = nextDemand?.id || null;
    await loadMatches();
  } catch (error) {
    if (error?.response?.status === 403) capabilityError.value = error;
    useAlert('Nao foi possivel carregar o console de demandas.');
  } finally {
    isLoading.value = false;
  }
};
const loadSnapshot = async () => {
  if (!canViewDemands.value) return;
  isLoadingSnapshot.value = true;
  try {
    const { data } = await PropertyMatchesAPI.operationalSnapshot({
      window_minutes: 1440,
    });
    snapshot.value = data;
  } catch {
    snapshot.value = null;
    useAlert('Nao foi possivel carregar o snapshot P11.');
  } finally {
    isLoadingSnapshot.value = false;
  }
};
const loadConsole = async () => {
  if (!canViewDemands.value) {
    capabilityError.value = { status: 403 };
    demands.value = [];
    matches.value = [];
    snapshot.value = null;
    return;
  }
  await Promise.all([loadDemands(), loadSnapshot()]);
};
const applyFilters = () => {
  filters.value.page = 1;
  loadDemands();
};
const runDemandAction = async (demand, action) => {
  if (!demand || !canRun(demand, action)) return;

  isMutating.value = true;
  mutatingKey.value = `${action}:${demand.id}`;
  try {
    if (action === 'pause') await PropertyDemandsAPI.pause(demand.id);
    if (action === 'resume') await PropertyDemandsAPI.resume(demand.id);
    if (action === 'recompute_matches')
      await PropertyDemandsAPI.recomputeMatches(demand.id);
    if (action === 'supersede') {
      await PropertyDemandsAPI.supersede(demand.id, {
        reason: bulkReason.value || 'property_demands_console_supersede',
      });
    }
    useAlert('Demanda atualizada.');
    await loadConsole();
  } catch {
    useAlert('Nao foi possivel executar a acao da demanda.');
  } finally {
    isMutating.value = false;
    mutatingKey.value = '';
  }
};
const runBulkAction = async operation => {
  if (!selectedCount.value) return;

  isMutating.value = true;
  mutatingKey.value = `bulk:${operation}`;
  try {
    await PropertyDemandsAPI.bulkAction(operation, selectedIdsArray(), {
      reason: bulkReason.value || `property_demands_console_${operation}`,
    });
    clearSelection();
    useAlert('Acao em lote processada.');
    await loadConsole();
  } catch {
    useAlert('Nao foi possivel processar a acao em lote.');
  } finally {
    isMutating.value = false;
    mutatingKey.value = '';
  }
};
const redispatchAlerts = async demand => {
  if (!demand) return;

  isMutating.value = true;
  mutatingKey.value = `redispatch:${demand.id}`;
  try {
    const latestMatch = matches.value.find(
      match => match.property_demand_id === demand.id
    );
    await PropertyDemandsAPI.redispatchAlerts(
      demand.id,
      latestMatch ? { property_match_id: latestMatch.id } : {}
    );
    useAlert('Redispatch P12 avaliado.');
    await loadConsole();
  } catch {
    useAlert('Nao foi possivel avaliar o redispatch P12.');
  } finally {
    isMutating.value = false;
    mutatingKey.value = '';
  }
};

onMounted(loadConsole);
</script>

<template>
  <!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
  <main class="min-h-screen bg-lais-bg text-lais-text">
    <div v-if="!canViewDemands" class="px-4 py-8 sm:px-6 lg:px-10">
      <LaisPermissionState variant="denied" />
    </div>
    <template v-else>
      <LaisHeader title="Demandas" title-badge="P10/P11">
        <template #actions>
          <LaisPrimaryButton
            icon="i-lucide-refresh-cw"
            variant="ghost"
            :is-loading="isLoading || isLoadingSnapshot"
            @click="loadConsole"
          >
            Atualizar
          </LaisPrimaryButton>
        </template>
      </LaisHeader>

      <section class="px-4 pb-8 sm:px-6 lg:px-10">
        <div class="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
          <article
            v-for="stat in stats"
            :key="stat.key"
            class="flex min-w-0 items-center gap-3 rounded-lais border border-lais-border-soft bg-white p-4 shadow-lais-card"
          >
            <span
              class="grid size-10 shrink-0 place-items-center rounded-full bg-[#EAF6F1] text-[#176B55]"
              aria-hidden="true"
            >
              <span class="size-5" :class="stat.icon" />
            </span>
            <div class="min-w-0">
              <p
                class="text-[12px] font-semibold uppercase text-lais-text-muted"
              >
                {{ stat.label }}
              </p>
              <strong class="text-[22px] leading-tight text-lais-text">
                {{ stat.value }}
              </strong>
            </div>
          </article>
        </div>

        <section
          class="mt-4 flex flex-col gap-3 rounded-lais border border-lais-border-soft bg-white p-3 shadow-lais-card"
        >
          <div class="grid gap-3 md:grid-cols-5">
            <label
              class="flex min-w-0 flex-col gap-1 text-[12px] font-semibold text-lais-text-muted"
            >
              Status
              <select
                v-model="filters.status"
                class="min-h-10 rounded-lais border border-lais-border-soft bg-white px-3 text-[14px] text-lais-text"
                @change="applyFilters"
              >
                <option value="">Todos</option>
                <option value="active">Ativa</option>
                <option value="paused">Pausada</option>
                <option value="satisfied">Satisfeita</option>
                <option value="expired">Expirada</option>
                <option value="discarded">Descartada</option>
              </select>
            </label>
            <label
              class="flex min-w-0 flex-col gap-1 text-[12px] font-semibold text-lais-text-muted"
            >
              Operacao
              <select
                v-model="filters.modalidade"
                class="min-h-10 rounded-lais border border-lais-border-soft bg-white px-3 text-[14px] text-lais-text"
                @change="applyFilters"
              >
                <option value="">Todas</option>
                <option value="venda">Compra</option>
                <option value="aluguel">Locacao</option>
                <option value="temporada">Temporada</option>
              </select>
            </label>
            <label
              class="flex min-w-0 flex-col gap-1 text-[12px] font-semibold text-lais-text-muted"
            >
              Objetivo
              <select
                v-model="filters.objective"
                class="min-h-10 rounded-lais border border-lais-border-soft bg-white px-3 text-[14px] text-lais-text"
                @change="applyFilters"
              >
                <option value="">Todos</option>
                <option value="home">Moradia</option>
                <option value="investment">Investimento</option>
                <option value="family">Familia</option>
                <option value="relocation">Mudanca</option>
                <option value="temporary">Temporario</option>
                <option value="other">Outro</option>
              </select>
            </label>
            <label
              class="flex min-w-0 flex-col gap-1 text-[12px] font-semibold text-lais-text-muted"
            >
              Fonte
              <select
                v-model="filters.source"
                class="min-h-10 rounded-lais border border-lais-border-soft bg-white px-3 text-[14px] text-lais-text"
                @change="applyFilters"
              >
                <option value="">Todas</option>
                <option value="manual">Manual</option>
                <option value="conversation">Conversa</option>
                <option value="portal_lead">Portal</option>
                <option value="profiling">Profiling</option>
                <option value="api">API</option>
                <option value="import">Importacao</option>
              </select>
            </label>
            <label
              class="flex min-w-0 flex-col gap-1 text-[12px] font-semibold text-lais-text-muted"
            >
              Busca
              <input
                v-model="filters.q"
                type="search"
                class="min-h-10 rounded-lais border border-lais-border-soft bg-white px-3 text-[14px] text-lais-text"
                placeholder="Cliente, titulo ou chave"
                @keydown.enter="applyFilters"
              />
            </label>
          </div>

          <div class="flex min-w-0 flex-wrap items-end gap-2">
            <label
              class="min-w-[240px] flex-1 text-[12px] font-semibold text-lais-text-muted"
            >
              Motivo operacional
              <input
                v-model="bulkReason"
                type="text"
                class="mt-1 min-h-10 w-full rounded-lais border border-lais-border-soft bg-white px-3 text-[14px] text-lais-text"
                placeholder="Opcional"
              />
            </label>
            <LaisPrimaryButton
              icon="i-lucide-search"
              variant="ghost"
              @click="applyFilters"
            >
              Filtrar
            </LaisPrimaryButton>
          </div>
        </section>

        <section
          v-if="selectedCount"
          class="mt-4 flex flex-wrap items-center justify-between gap-3 rounded-lais border border-[#176B55]/30 bg-[#EAF6F1] p-3 text-[#176B55]"
        >
          <strong class="text-[14px]">{{ selectedCount }} selecionadas</strong>
          <div class="flex min-w-0 flex-wrap gap-2">
            <LaisPrimaryButton
              v-for="action in [
                'pause',
                'resume',
                'recompute_matches',
                'supersede',
              ]"
              :key="`bulk-${action}`"
              :icon="ACTION_ICONS[action]"
              size="sm"
              variant="ghost"
              :is-loading="mutatingKey === `bulk:${action}`"
              :disabled="isMutating"
              @click="runBulkAction(action)"
            >
              {{ ACTION_LABELS[action] }}
            </LaisPrimaryButton>
            <LaisPrimaryButton
              icon="i-lucide-x"
              size="sm"
              variant="link"
              @click="clearSelection"
            >
              Limpar
            </LaisPrimaryButton>
          </div>
        </section>

        <section
          class="mt-4 grid min-w-0 gap-4 xl:grid-cols-[minmax(0,1fr)_360px]"
        >
          <section
            class="min-w-0 rounded-lais border border-lais-border-soft bg-white shadow-lais-card"
          >
            <div
              v-if="isLoading && !hasDemands"
              class="p-6 text-[14px] text-lais-text-muted"
            >
              Carregando demandas...
            </div>
            <div
              v-else-if="!hasDemands"
              class="p-6 text-[14px] text-lais-text-muted"
            >
              Nenhuma demanda encontrada.
            </div>
            <div v-else class="overflow-x-auto">
              <table
                class="min-w-full divide-y divide-lais-border-soft text-left"
              >
                <thead
                  class="bg-[#F8FAFB] text-[12px] uppercase text-lais-text-muted"
                >
                  <tr>
                    <th class="w-12 px-4 py-3">
                      <input
                        type="checkbox"
                        :checked="selectedCount === demands.length"
                        class="size-4 rounded border-lais-border-soft"
                        aria-label="Selecionar demandas"
                        @change="toggleAll"
                      />
                    </th>
                    <th class="px-4 py-3">Demanda</th>
                    <th class="px-4 py-3">Cliente</th>
                    <th class="px-4 py-3">Criterios</th>
                    <th class="px-4 py-3">Status</th>
                    <th class="px-4 py-3">Acoes</th>
                  </tr>
                </thead>
                <tbody class="divide-y divide-lais-border-soft text-[14px]">
                  <tr
                    v-for="demand in demands"
                    :key="demand.id"
                    class="align-top transition hover:bg-[#F8FAFB]"
                    :class="
                      selectedDemandId === demand.id
                        ? 'bg-[#EAF6F1]/50'
                        : 'bg-white'
                    "
                  >
                    <td class="px-4 py-4">
                      <input
                        type="checkbox"
                        :checked="selectedIds.has(demand.id)"
                        class="size-4 rounded border-lais-border-soft"
                        :aria-label="`Selecionar demanda ${demand.id}`"
                        @change="toggleDemand(demand.id)"
                      />
                    </td>
                    <td class="min-w-[240px] px-4 py-4">
                      <button
                        type="button"
                        class="block max-w-[280px] text-left"
                        @click="selectDemand(demand)"
                      >
                        <span
                          class="block truncate font-semibold text-lais-text"
                        >
                          {{ demand.title || demand.demand_key }}
                        </span>
                        <span
                          class="block truncate text-[12px] text-lais-text-muted"
                        >
                          #{{ demand.id }} ·
                          {{ modalidadeLabel(demand.modalidade) }} ·
                          {{ objectiveLabel(demand.objective) }}
                        </span>
                      </button>
                    </td>
                    <td class="min-w-[200px] px-4 py-4">
                      <RouterLink
                        v-if="demand.contact_id"
                        :to="
                          account.accountScopedUrl(
                            `history/${demand.contact_id}`
                          )
                        "
                        class="inline-flex max-w-[220px] items-center gap-2 text-lais-link hover:text-lais-link-hover"
                      >
                        <span
                          class="i-lucide-user-round size-4 shrink-0"
                          aria-hidden="true"
                        />
                        <span class="truncate">{{
                          demand.contact?.name || `Contato ${demand.contact_id}`
                        }}</span>
                      </RouterLink>
                    </td>
                    <td class="min-w-[240px] px-4 py-4">
                      <p class="truncate text-lais-text-body">
                        {{ demandBudget(demand) }}
                      </p>
                      <p class="truncate text-[12px] text-lais-text-muted">
                        {{ demandRegion(demand) }}
                      </p>
                    </td>
                    <td class="px-4 py-4">
                      <span
                        class="inline-flex rounded-full px-2 py-1 text-[11px] font-bold"
                        :class="badgeClass(STATUS_CLASSES, demand.status)"
                      >
                        {{ statusLabel(demand.status) }}
                      </span>
                      <p class="mt-1 text-[12px] text-lais-text-muted">
                        {{ dateTime(demand.updated_at) }}
                      </p>
                    </td>
                    <td class="min-w-[260px] px-4 py-4">
                      <div class="flex flex-wrap gap-2">
                        <button
                          v-for="action in [
                            'pause',
                            'resume',
                            'recompute_matches',
                            'supersede',
                          ]"
                          :key="`${demand.id}-${action}`"
                          type="button"
                          class="grid size-9 place-items-center rounded-lais border border-lais-border-soft text-lais-text-muted transition hover:border-[#176B55] hover:text-[#176B55] disabled:cursor-not-allowed disabled:opacity-40"
                          :disabled="!canRun(demand, action) || isMutating"
                          :title="ACTION_LABELS[action]"
                          :aria-label="ACTION_LABELS[action]"
                          @click="runDemandAction(demand, action)"
                        >
                          <span
                            class="size-4"
                            :class="
                              mutatingKey === `${action}:${demand.id}`
                                ? 'i-lucide-loader-circle animate-spin'
                                : ACTION_ICONS[action]
                            "
                            aria-hidden="true"
                          />
                        </button>
                        <button
                          type="button"
                          class="grid size-9 place-items-center rounded-lais border border-lais-border-soft text-lais-text-muted transition hover:border-[#2457A6] hover:text-[#2457A6] disabled:cursor-not-allowed disabled:opacity-40"
                          :disabled="isMutating"
                          title="Redispatch P12"
                          aria-label="Redispatch P12"
                          @click="redispatchAlerts(demand)"
                        >
                          <span
                            class="size-4"
                            :class="
                              mutatingKey === `redispatch:${demand.id}`
                                ? 'i-lucide-loader-circle animate-spin'
                                : 'i-lucide-send'
                            "
                            aria-hidden="true"
                          />
                        </button>
                      </div>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </section>

          <aside class="flex min-w-0 flex-col gap-4">
            <section
              class="rounded-lais border border-lais-border-soft bg-white p-4 shadow-lais-card"
            >
              <div class="flex items-start justify-between gap-3">
                <div>
                  <h2 class="text-[16px] font-semibold text-lais-text">
                    Observabilidade P11
                  </h2>
                  <p class="text-[12px] text-lais-text-muted">
                    {{ snapshot?.runbook_key || 'P11-RB-MATCHING' }}
                  </p>
                </div>
                <button
                  type="button"
                  class="grid size-9 place-items-center rounded-lais border border-lais-border-soft text-lais-text-muted transition hover:text-[#176B55]"
                  aria-label="Atualizar snapshot"
                  @click="loadSnapshot"
                >
                  <span
                    class="size-4"
                    :class="
                      isLoadingSnapshot
                        ? 'i-lucide-loader-circle animate-spin'
                        : 'i-lucide-refresh-cw'
                    "
                    aria-hidden="true"
                  />
                </button>
              </div>

              <dl class="mt-4 grid grid-cols-2 gap-3 text-[13px]">
                <div>
                  <dt class="text-lais-text-muted">Matches</dt>
                  <dd class="font-semibold text-lais-text">
                    {{ snapshotMetrics.total_count ?? 0 }}
                  </dd>
                </div>
                <div>
                  <dt class="text-lais-text-muted">Freshness</dt>
                  <dd class="font-semibold text-lais-text">
                    {{ snapshotMetrics.freshness_minutes ?? 'no_data' }}
                  </dd>
                </div>
                <div>
                  <dt class="text-lais-text-muted">Explainability</dt>
                  <dd class="font-semibold text-lais-text">
                    {{ snapshotMetrics.explainability_percent ?? 'no_data' }}
                  </dd>
                </div>
                <div>
                  <dt class="text-lais-text-muted">Geografia</dt>
                  <dd class="font-semibold text-lais-text">
                    {{
                      snapshotMetrics.geography_breakdown_percent ?? 'no_data'
                    }}
                  </dd>
                </div>
              </dl>

              <div class="mt-4 flex flex-wrap gap-2">
                <span
                  v-for="row in sloRows"
                  :key="row.key"
                  class="rounded-full px-2 py-1 text-[11px] font-bold"
                  :class="badgeClass(SLO_CLASSES, row.value)"
                >
                  {{ row.label }}: {{ row.value || 'no_data' }}
                </span>
              </div>
            </section>

            <section
              class="rounded-lais border border-lais-border-soft bg-white p-4 shadow-lais-card"
            >
              <h2 class="text-[16px] font-semibold text-lais-text">
                Matches da demanda
              </h2>
              <p class="mt-1 truncate text-[12px] text-lais-text-muted">
                {{
                  selectedDemand?.title ||
                  selectedDemand?.demand_key ||
                  'Sem demanda selecionada'
                }}
              </p>

              <div
                v-if="isLoadingMatches"
                class="mt-4 text-[13px] text-lais-text-muted"
              >
                Carregando matches...
              </div>
              <div
                v-else-if="!selectedDemandMatches.length"
                class="mt-4 text-[13px] text-lais-text-muted"
              >
                Nenhum match persistido.
              </div>
              <ul v-else class="mt-4 space-y-3">
                <li
                  v-for="match in selectedDemandMatches"
                  :key="match.id"
                  class="rounded-lais border border-lais-border-soft p-3"
                >
                  <div class="flex items-center justify-between gap-3">
                    <strong class="text-[14px] text-lais-text">
                      Match #{{ match.id }}
                    </strong>
                    <span
                      class="rounded-full px-2 py-1 text-[11px] font-bold"
                      :class="
                        badgeClass(
                          SLO_CLASSES,
                          match.status === 'eligible' ? 'ok' : 'breached'
                        )
                      "
                    >
                      {{ match.status }}
                    </span>
                  </div>
                  <p class="mt-1 text-[13px] text-lais-text-body">
                    Score {{ match.score }} · listing v{{
                      match.listing_version
                    }}
                  </p>
                  <p class="mt-1 truncate text-[12px] text-lais-text-muted">
                    {{ match.algorithm_version }}
                  </p>
                </li>
              </ul>
            </section>
          </aside>
        </section>
      </section>
    </template>
  </main>
</template>
